import React, { useState, useEffect } from 'react';
import { Save, Gift, AlertCircle } from 'lucide-react';
import { getProjectCode } from '../../../utils/auth';
import { getBrandList, type BrandNode } from '../../../utils/brand';
import { getBrandMemberships, brandMembershipsBulkUpdate } from '../../../utils/membership';
import { getVoucherList } from '../../../utils/voucher';

/** 브랜드 트리 순서(depth-first)대로 브랜드 코드 배열 반환 */
function flattenBrandCodesInOrder(nodes: BrandNode[]): string[] {
  const out: string[] = [];
  function walk(n: BrandNode) {
    out.push(n.code);
    (n.subBrands || []).forEach(walk);
  }
  nodes.forEach(walk);
  return out;
}

/** 브랜드 트리에서 코드 → 이름 맵 생성 (교환권 없어도 브랜드명 표시용) */
function getBrandNameMap(nodes: BrandNode[]): Map<string, string> {
  const m = new Map<string, string>();
  function walk(n: BrandNode) {
    m.set(n.code, n.name);
    (n.subBrands || []).forEach(walk);
  }
  nodes.forEach(walk);
  return m;
}

const VOUCHER_EXCHANGE_LABELS: Record<string, string> = {
  STEP: '단계별',
  CHOICE: '선택',
};

export interface MembershipVoucherSettingTabProps {
  projectCode: string;
  membershipCode: string;
  /** 1세트(카드 당) 스탬프 수 (멤버십 정보에서 설정된 값) */
  stampSetCount?: number;
  /** 교환권 교환 방식 (멤버십 정보에서 설정된 값) */
  voucherExchangeMethod?: string;
  /** 부모에서 이미 조회한 매핑 브랜드 (있으면 getBrandMemberships 중복 호출 안 함) */
  parentBrandCodes?: string[] | null;
  onSaveSuccess?: () => void;
}

interface BrandWithVouchers {
  brandCode: string;
  brandName?: string;
  vouchers: { code: string; name: string; price: number }[];
}

interface VoucherSelection {
  voucherCode: string;
  brandCode: string;
  stampCount: number;
}

const MembershipVoucherSettingTab: React.FC<MembershipVoucherSettingTabProps> = ({
  projectCode,
  membershipCode,
  stampSetCount = 0,
  voucherExchangeMethod = '',
  parentBrandCodes,
  onSaveSuccess,
}) => {
  const [mappedBrands, setMappedBrands] = useState<string[]>([]);
  const [brandNameMap, setBrandNameMap] = useState<Map<string, string> | null>(null);
  const [brandVouchers, setBrandVouchers] = useState<BrandWithVouchers[]>([]);
  const [selections, setSelections] = useState<VoucherSelection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 1. 브랜드 트리 목록 조회 2. 브랜드 멤버십 목록+교환권설정 조회 3. 1의 순서대로 2에 존재하는 브랜드만 표시
  // parentBrandCodes === null이면 부모 로딩 대기
  useEffect(() => {
    if (parentBrandCodes === null) return;

    let ignore = false;

    const load = async () => {
      if (!projectCode || !membershipCode) return;
      try {
        setIsLoading(true);
        setError(null);

        const [brandTreeRes, brandsRes] = await Promise.all([
          getBrandList(projectCode),
          getBrandMemberships(projectCode, membershipCode),
        ]);
        if (ignore) return;

        const brandMemberships = brandsRes.brandMemberships || [];
        const membershipBrandSet = new Set(
          brandMemberships.map((b) => b.brandCode).filter(Boolean)
        );
        const orderedCodes = flattenBrandCodesInOrder(brandTreeRes.brands || []);
        const brandCodesInTreeOrder = orderedCodes.filter((c) => membershipBrandSet.has(c));
        setBrandNameMap(getBrandNameMap(brandTreeRes.brands || []));

        const initialSelections: VoucherSelection[] = [];
        for (const bm of brandMemberships) {
          const vs = bm.voucherSettings as
            | Array<{ voucherCode?: string; stampCount?: number }>
            | { items?: Array<{ voucherCode?: string; brandCode?: string; stampCount?: number }> }
            | undefined;
          const items = Array.isArray(vs)
            ? vs
            : Array.isArray((vs as any)?.items)
              ? (vs as any).items
              : [];
          const bCode = bm.brandCode;
          for (const it of items) {
            const vCode = (it as any)?.voucherCode;
            if (!vCode) continue;
            const stamp = typeof (it as any).stampCount === 'number' ? (it as any).stampCount : 0;
            initialSelections.push({
              voucherCode: vCode,
              brandCode: (it as any)?.brandCode ?? bCode,
              stampCount: stamp > 0 ? stamp : 1,
            });
          }
        }

        setMappedBrands(brandCodesInTreeOrder);
        setSelections(initialSelections);
      } catch (e) {
        if (ignore) return;
        console.error('브랜드 멤버십 목록 로드 오류:', e);
        setError(e instanceof Error ? e.message : '데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [projectCode, membershipCode, parentBrandCodes]);

  // 브랜드별 교환권 목록 로드 (브랜드명: 1번에서 구한 brandNameMap 사용 → 교환권 없어도 브랜드명 표시)
  useEffect(() => {
    if (mappedBrands.length === 0) {
      setBrandVouchers([]);
      return;
    }

    let ignore = false;
    const nameMap = brandNameMap;

    const loadVouchers = async () => {
      const results: BrandWithVouchers[] = [];
      for (const brandCode of mappedBrands) {
        if (ignore) return;
        try {
          const res = await getVoucherList(projectCode, { brandCode, status: 'ACTIVE' });
          if (ignore) return;
          const firstVoucher = res.vouchers?.[0] as { brandName?: string } | undefined;
          results.push({
            brandCode,
            brandName: nameMap?.get(brandCode) ?? firstVoucher?.brandName,
            vouchers: (res.vouchers || []).map((v) => ({
              code: v.code,
              name: v.name,
              price: v.price,
            })),
          });
        } catch {
          if (ignore) return;
          results.push({
            brandCode,
            brandName: nameMap?.get(brandCode),
            vouchers: [],
          });
        }
      }
      if (!ignore) setBrandVouchers(results);
    };
    loadVouchers();
    return () => {
      ignore = true;
    };
  }, [projectCode, mappedBrands, brandNameMap]);

  const isChecked = (voucherCode: string, brandCode: string): boolean => {
    return selections.some((s) => s.voucherCode === voucherCode && s.brandCode === brandCode);
  };

  const toggleCheck = (voucherCode: string, brandCode: string) => {
    setSelections((prev) => {
      const filtered = prev.filter((s) => !(s.voucherCode === voucherCode && s.brandCode === brandCode));
      if (filtered.length === prev.length) {
        return [...prev, { voucherCode, brandCode, stampCount: 1 }];
      }
      return filtered;
    });
  };

  const updateStampCount = (voucherCode: string, brandCode: string, stampCount: number) => {
    setSelections((prev) => {
      const filtered = prev.filter((s) => !(s.voucherCode === voucherCode && s.brandCode === brandCode));
      if (stampCount > 0) {
        return [...filtered, { voucherCode, brandCode, stampCount }];
      }
      return filtered;
    });
  };

  const getStampCount = (voucherCode: string, brandCode: string): number => {
    const s = selections.find((x) => x.voucherCode === voucherCode && x.brandCode === brandCode);
    return s?.stampCount ?? 0;
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setSaveError(null);
      const checked = selections.filter((s) => s.stampCount > 0);
      const byBrand = new Map<string, Array<{ voucherCode: string; stampCount: number }>>();
      for (const s of checked) {
        const list = byBrand.get(s.brandCode) ?? [];
        list.push({ voucherCode: s.voucherCode, stampCount: s.stampCount });
        byBrand.set(s.brandCode, list);
      }
      const payload = mappedBrands.map((brandCode) => ({
        membershipCode,
        brandCode,
        voucherSettings: byBrand.get(brandCode) ?? [],
      }));
      await brandMembershipsBulkUpdate(projectCode, payload);
      onSaveSuccess?.();
    } catch (e) {
      console.error('교환권 설정 저장 오류:', e);
      setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-medium text-red-800">오류</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (mappedBrands.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-600">
        매핑된 브랜드가 없습니다. 브랜드 매핑 탭에서 먼저 브랜드를 선택해주세요.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {saveError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">저장 오류</h3>
            <p className="text-sm text-red-700 mt-1">{saveError}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Gift className="w-5 h-5 mr-2" />
            교환권 설정
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            매핑된 브랜드별로 교환권을 선택하고, 스탬프 개수를 입력해주세요.
          </p>
          {(stampSetCount > 0 || voucherExchangeMethod) && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
              {stampSetCount > 0 && (
                <span>1세트 스탬프 수: <strong className="text-gray-700">{stampSetCount}개</strong></span>
              )}
              {voucherExchangeMethod && (
                <span>교환권 교환 방식: <strong className="text-gray-700">{VOUCHER_EXCHANGE_LABELS[voucherExchangeMethod] ?? voucherExchangeMethod}</strong></span>
              )}
            </div>
          )}
        </div>
        <div className="px-4 lg:px-6 py-4 space-y-6">
          {brandVouchers.map((brand) => (
            <div key={brand.brandCode} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">
                  {brand.brandName ? `${brand.brandName} (${brand.brandCode})` : brand.brandCode}
                </h3>
              </div>
              <div className="p-4">
              {brand.vouchers.length === 0 ? (
                <p className="text-sm text-gray-500">등록된 교환권이 없습니다.</p>
              ) : (
                <div className="space-y-3">
                  {brand.vouchers.map((v) => {
                    const checked = isChecked(v.code, brand.brandCode);
                    return (
                      <div
                        key={v.code}
                        className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCheck(v.code, brand.brandCode)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-900">{v.name}</span>
                          <span className="text-gray-500 text-sm ml-2">({v.code})</span>
                        </div>
                        {checked && (
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">스탬프 수:</label>
                            <input
                              type="number"
                              min={1}
                              max={999}
                              value={getStampCount(v.code, brand.brandCode) || ''}
                              onChange={(e) =>
                                updateStampCount(
                                  v.code,
                                  brand.brandCode,
                                  parseInt(e.target.value, 10) || 0
                                )
                              }
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="1"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {isSubmitting ? '저장 중...' : '교환권 설정 저장'}
        </button>
      </div>
    </div>
  );
};

export default MembershipVoucherSettingTab;
